import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { setReturningCookie } from '../utils/cookies';

// Auth routes are at /auth/*, not /api/auth/*, so strip the /api suffix.
// Use the origin from the full URL to avoid relative-path issues.
function getAuthBase(): string {
  const apiUrl = process.env.REACT_APP_API_URL;
  if (!apiUrl) return 'http://localhost:5000';
  try {
    const url = new URL(apiUrl, window.location.origin);
    url.pathname = url.pathname.replace(/\/api\/?$/, '');
    return url.origin + url.pathname.replace(/\/$/, '');
  } catch {
    return apiUrl.replace(/\/api\/?$/, '');
  }
}
const API_BASE = getAuthBase();

// Store CSRF token in memory (cookies from cross-origin responses aren't readable via document.cookie)
let csrfToken = '';

export function getCsrfToken(): string {
  return csrfToken;
}

export async function refreshCsrfToken(): Promise<void> {
  return fetchCsrfToken();
}

async function fetchCsrfToken(): Promise<void> {
  try {
    const response = await fetch(`${API_BASE}/auth/csrf-token`, { credentials: 'include' });
    if (response.ok) {
      const data = await response.json();
      csrfToken = data.csrf_token || '';
      console.debug('CSRF token fetched successfully');
    } else {
      console.warn('CSRF token fetch failed:', response.status);
    }
  } catch (err) {
    console.warn('CSRF token fetch error:', err);
  }
}

interface User {
  id: number;
  email: string;
  name: string | null;
  logged_in: boolean;
  is_admin: boolean;
  stripe_test_mode?: boolean;
  subscription_status: 'none' | 'active' | 'past_due' | 'canceled' | 'trialing';
  subscription_period_end: string | null;
  plan_interval: 'month' | 'year' | null;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  register: (userData: { email: string; password: string; name?: string }) => Promise<{ success: boolean; error?: string }>;
  checkAuth: () => Promise<void>;
  isAuthenticated: boolean;
  isAdmin: boolean;
  hasActiveSubscription: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch CSRF token on initial load so login/register can work
    fetchCsrfToken().then(() => checkAuth());
  }, []);

  const checkAuth = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);

      const response = await fetch(`${API_BASE}/auth/me`, {
        credentials: 'include',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const userData = await response.json();
        if (userData.logged_in) {
          if (userData.is_suspended) {
            // Force logout suspended users
            await fetch(`${API_BASE}/auth/logout`, { method: 'GET', credentials: 'include' });
            setUser(null);
            alert('Your account has been suspended. Please contact support.');
            return;
          }
          setUser(userData);
        } else {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string, rememberMe: boolean = false) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);

      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
        credentials: 'include',
        body: JSON.stringify({ email, password, rememberMe }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        // Refresh CSRF token (stored in memory from JSON response)
        await fetchCsrfToken();
        setReturningCookie();
        await checkAuth();
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: error.error || 'Login failed' };
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return { success: false, error: 'Server is taking too long to respond. Please try again.' };
      }
      return { success: false, error: `Network error: ${error.message}` };
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'GET',
        credentials: 'include'
      });
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
      setUser(null);
    }
  };

  const register = async (userData: { email: string; password: string; name?: string }) => {
    try {
      const response = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
        credentials: 'include',
        body: JSON.stringify(userData)
      });

      if (response.ok) {
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: error.error || 'Registration failed' };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      register,
      checkAuth,
      isAuthenticated: !!user?.logged_in,
      isAdmin: !!user?.is_admin,
      hasActiveSubscription: user?.subscription_status === 'active' || user?.subscription_status === 'trialing'
    }}>
      {children}
    </AuthContext.Provider>
  );
};
